import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Unlock, Lock, WifiOff } from "lucide-react";
import api from "../lib/api";
import MobileShell, { LocationSelect, useMobileLocation, Card, Empty, Skeleton } from "./MobileShell";

function LockRow({ lock, placeId, onDone }) {
  const [busy, setBusy] = useState(false);
  const unlock = async () => {
    setBusy(true);
    try {
      await api.post(`/kisi/locks/${lock.id}/unlock`, null, { params: { place_id: placeId } });
      toast.success(`${lock.name} unlocked`);
      navigator.vibrate?.(40);
      setTimeout(onDone, 1200);
    } catch (e) { toast.error(`Couldn't unlock ${lock.name}`, { description: e.userMessage }); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-3 px-4 py-3" data-testid={`m-lock-${lock.id}`}>
      <span className="flex-1 min-w-0">
        <span className="block text-base truncate">{lock.name}</span>
        <span className={`text-xs ${lock.online ? "text-brand-tertiary" : "text-brand-neg"}`}>{!lock.online ? "Offline" : lock.unlocked ? "Unlocked now" : "Locked"}</span>
      </span>
      <button onClick={unlock} disabled={busy || !lock.online} data-testid={`m-unlock-${lock.id}`}
        className="inline-flex items-center gap-2 min-h-[48px] px-5 rounded-xl bg-brand-primary text-brand-page text-base font-medium active:scale-95 transition-transform disabled:opacity-40">
        {!lock.online ? <WifiOff className="h-4 w-4" /> : busy ? <Lock className="h-4 w-4 animate-pulse" /> : <Unlock className="h-4 w-4" />} {busy ? "…" : "Unlock"}
      </button>
    </div>
  );
}

export default function MDoors() {
  const { location } = useMobileLocation();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(() => {
    api.get("/kisi/doors", { params: location ? { location } : {} })
      .then(({ data }) => { setData(data); setError(null); })
      .catch((e) => setError(e.userMessage));
  }, [location]);
  useEffect(() => { setData(null); load(); }, [load]);
  return (
    <MobileShell title="Unlock a door">
      <LocationSelect />
      {error && <p className="text-sm text-brand-neg" data-testid="m-doors-error">Doors couldn't load: {error}</p>}
      {!data && !error ? <Skeleton /> : data && !data.configured ? <Empty>Door controls aren't set up yet.</Empty>
        : data && !data.locations.length ? <Empty>No doors for this location.</Empty>
        : data?.locations.map((l) => (
          <div key={l.place_id} className="space-y-2" data-testid={`m-place-${l.place_id}`}>
            <p className="text-xs uppercase tracking-[0.15em] text-brand-tertiary px-1">{l.location}</p>
            <Card className="divide-y divide-brand-border">{l.locks.map((k) => <LockRow key={k.id} lock={k} placeId={l.place_id} onDone={load} />)}</Card>
          </div>
        ))}
    </MobileShell>
  );
}
